import { createMemo } from 'solid-js'
import {
   habitHasTag,
   MAX_HABIT_TAG_LENGTH,
   normalizeHabitTags,
} from '@fasting-timer/shared'

import FieldLabel from '../ui/FieldLabel'
import MultiSelect from '../ui/MultiSelect'
import { useI18n } from '../../i18n'
import { getHabitTagSummaries, habits } from '../../state/habitsStore'

/**
 * Renders the habit form's tag picker: existing tags are suggested, and typed
 * text that matches none becomes a new tag.
 *
 * @param props - Field id prefix, the selected tags, and the change handler.
 * @returns The field element.
 */
const HabitTagsField = (props: {
   prefix: string
   value: string[]
   onChange: (tags: string[]) => void
}) => {
   const t = useI18n()
   const knownTags = createMemo(() =>
      getHabitTagSummaries(habits()).map(summary => summary.tag),
   )
   const options = createMemo(() => {
      const unsaved = props.value.filter(tag => !habitHasTag(knownTags(), tag))
      return [...knownTags(), ...unsaved].map(tag => ({ id: tag, name: tag }))
   })
   // Option ids use the known spelling, which may differ in case from this habit's.
   const selectedIds = createMemo(() =>
      props.value.map(
         tag =>
            knownTags().find(known => known.toLowerCase() === tag.toLowerCase()) ?? tag,
      ),
   )

   /**
    * Adds or removes a tag.
    *
    * @param tag - The tag.
    */
   const toggle = (tag: string) =>
      props.onChange(
         habitHasTag(props.value, tag) ?
            props.value.filter(selected => selected.toLowerCase() !== tag.toLowerCase())
         :  normalizeHabitTags([...props.value, tag]),
      )

   return (
      <div>
         <FieldLabel for={`${props.prefix}-habit-tags`}>{t('Tags')}</FieldLabel>
         <p class="mb-2 text-xs text-ink-faint">
            {t('Tagged tasks can be paused and resumed together.')}
         </p>
         <MultiSelect
            id={`${props.prefix}-habit-tags`}
            options={options()}
            selectedIds={selectedIds()}
            placeholder={t('Add a tag')}
            emptyLabel={t('Type to create a tag')}
            removeLabel={name => t('Remove {name}', { name })}
            createLabel={name =>
               t('Create tag "{name}"', { name: name.slice(0, MAX_HABIT_TAG_LENGTH) })
            }
            onToggle={toggle}
            onCreate={toggle}
         />
      </div>
   )
}

export default HabitTagsField
